import type { ReactNode } from "react";
import { getSession } from "../api/queries.js";
import { useQuery } from "../api/useQuery.js";
import { ConsentScreen } from "../screens/consent/ConsentScreen.js";

/**
 * Holds the workspace back until the signed-in person has agreed to the current
 * Terms of Use and Privacy Policy.
 *
 * Three deliberate choices:
 *
 * - **Nothing renders while the session loads.** Otherwise the workspace, and
 *   the requests it makes for someone's data, would start before we know they
 *   have agreed.
 * - **A session that fails to load does not lock anyone out.** The connection
 *   banner already tells them the app cannot reach the server, and a blank screen
 *   with no explanation is worse. Consent is still recorded and checked the next
 *   time the session loads.
 * - **Impersonating skips the gate.** A superadmin looking at someone's account
 *   is not that person and cannot agree for them (the API refuses); the gate
 *   applies to the superadmin's own sign-in.
 */
export function RequireConsent({ children }: { children: ReactNode }) {
  const session = useQuery(getSession, undefined);

  if (session.loading) return null;

  const consent = session.data?.consent;
  if (consent?.required && !session.data?.impersonating) {
    return <ConsentScreen version={consent.currentVersion} />;
  }

  return children;
}
